/**
 * Pictures that stand in the text they were added to: adding one puts its
 * `[picture N]` line at the cursor, removing one takes its line out and
 * renumbers the later ones. Shared by the staff reply box and by both of the
 * reporter's composers (a new report, a reply in a conversation).
 */
import { type Dispatch, type RefObject, type SetStateAction, useRef } from "react";
import type { ComposerCaret } from "./support-composer-editor";
import type { DraftPictures } from "./support-draft-pictures";
import { insertPictureToken, removePictureToken } from "./support-picture-tokens";

export interface PicturesInText {
	/** Goes on the composer, so the cursor can be read when a picture arrives. */
	inputRef: RefObject<ComposerCaret | null>;
	/** Adds the picture and its line; returns the picture's key. */
	add: (blob: Blob) => number;
	/** Adds the picture and its line, then opens it for markup. */
	addAndEdit: (blob: Blob) => void;
	remove: (key: number) => void;
}

export const usePicturesInText = (
	draft: DraftPictures,
	text: string,
	setText: Dispatch<SetStateAction<string>>
): PicturesInText => {
	const inputRef = useRef<ComposerCaret>(null);

	// An input that was never focused has no cursor yet: the end of the text.
	const add = (blob: Blob): number => {
		const cursor = inputRef.current?.offset() ?? text.length;
		const number = draft.pictures.length + 1;
		setText((previous) => insertPictureToken(previous, cursor, number));
		return draft.add(blob);
	};

	return {
		inputRef,
		add,
		// The edited picture replaces the original in place: its number stays.
		addAndEdit: (blob) => draft.openEditor(add(blob)),
		remove: (key) => {
			const number =
				draft.pictures.findIndex((picture) => picture.key === key) + 1;
			setText((previous) => removePictureToken(previous, number));
			draft.remove(key);
		},
	};
};
